import React, { useState, useEffect } from 'react';
import {
  Pane,
  Heading,
  Tab,
  Tablist,
  Badge,
  Button,
  Spinner,
  toaster,
  Strong,
  Text,
  IconButton,
  Pill,
  Icon
} from 'evergreen-ui';
import { useProject } from '../../context/ProjectContext';
import api from '../../services/api';
import NoteEditor from '../Notes/NoteEditor';

const SidePanel = ({ selectedNode, onClose }) => {
  const { currentProject: project } = useProject();
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [nodeData, setNodeData] = useState(null);
  const [overview, setOverview] = useState(null);

  const fetchNodeData = async () => {
    if (!selectedNode || !project?.id) return;
    console.log('Selected Node:', selectedNode);
    console.log('Project ID:', project.id);
    
    try {
      setLoading(true);
      
      // Always fetch basic node data
      const dataUrl = `/projects/${project.id}/graph/nodes/${selectedNode.type}/${selectedNode.id}`;
      console.log('Fetching data from:', dataUrl);
      const dataResponse = await api.get(dataUrl);
      console.log('Data response:', dataResponse);
      
      // Only fetch overview for nodes that need it (not project, country, org, or asn)
      const needsOverview = !['project', 'country', 'org', 'asn'].includes(selectedNode.type);
      console.log('Needs overview:', needsOverview);
      
      let overviewResponse = null;
      if (needsOverview) {
        const overviewUrl = `/projects/${project.id}/graph/nodes/${selectedNode.type}/${selectedNode.id}/overview`;
        console.log('Fetching overview from:', overviewUrl);
        overviewResponse = await api.get(overviewUrl);
        console.log('Overview response:', overviewResponse);
      }

      if (dataResponse.data) {
        const nodeDetails = dataResponse.data;
        // Ensure all required fields exist
        nodeDetails.findings = nodeDetails.findings || [];
        nodeDetails.notes = nodeDetails.notes || [];
        nodeDetails.ports = nodeDetails.ports || [];
        nodeDetails.task_history = nodeDetails.task_history || [];
        nodeDetails.sources = nodeDetails.sources || [];
        setNodeData(nodeDetails);
      }

      if (overviewResponse?.data) {
        setOverview(overviewResponse.data);
      } else {
        // Clear overview for nodes that don't need it
        setOverview(null);
      }
    } catch (error) {
      console.error('Error fetching node data:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      let errorMessage = 'Unknown error occurred';
      
      if (error.response) {
        // Server responded with an error status
        errorMessage = error.response.data?.detail || error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'No response from server - check network connection';
      } else {
        // Error in request setup
        errorMessage = error.message || 'Request setup error';
      }
      
      toaster.danger(`Failed to fetch node data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedNode) {
      fetchNodeData();
    } else {
      setNodeData(null);
      setOverview(null);
    }
  }, [selectedNode, project?.id]);

  const handleScanTask = async (taskType) => {
    try {
      await api.post(`/projects/${project.id}/tasks`, {
        task_type: taskType,
        name: `${taskType}_${selectedNode.label}`,
        target: selectedNode.label,
        parameters: {}
      });
      toaster.success(`Started ${taskType} scan`);
      // Refresh data after starting scan
      setTimeout(fetchNodeData, 1000);
    } catch (error) {
      toaster.danger('Failed to start scan');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return '#8B0000';  // Dark red
      case 'high':
        return '#FF6B6B';  // Slightly red
      case 'medium':
        return '#FFA500';  // Slightly orange
      case 'low':
        return '#90EE90';  // Slightly green
      case 'info':
        return '#ADD8E6';  // Slightly blue
      case 'none':
      default:
        return '#D3D3D3';  // Light grey
    }
  };

  const renderOverviewSummary = () => {
    // Don't render overview for project, country, org, or asn nodes
    if (!overview || ['project', 'country', 'org', 'asn'].includes(selectedNode?.type)) return null;

    return (
    <Pane>
        <Heading size={500} marginBottom={16}>Overview</Heading>
        
        {/* Summary Pills */}
        <Pane display="flex" flexWrap="wrap" gap={8} marginBottom={16}>
          <Pill
            display="inline-flex"
            alignItems="center"
            backgroundColor={getSeverityColor(nodeData?.details?.severity)}
            color={nodeData?.details?.severity === 'critical' ? 'white' : 'black'}
            fontSize={12}
            paddingX={12}
            paddingY={8}
            borderRadius={12}
          >
            <Icon icon="warning-sign" marginRight={8} size={14} />
            <Strong>{overview.findings_count}</Strong>
            <Text marginLeft={4}>
              {overview.findings_count === 1 ? 'Finding' : 'Findings'}
            </Text>
          </Pill>

          <Pill
            display="inline-flex"
            alignItems="center"
            backgroundColor="#E4E7EB"
            color="default"
            fontSize={12}
            paddingX={12}
            paddingY={8}
            borderRadius={12}
          >
            <Icon icon="document" marginRight={8} size={14} />
            <Strong>{overview.notes_count}</Strong>
            <Text marginLeft={4}>
              {overview.notes_count === 1 ? 'Note' : 'Notes'}
            </Text>
          </Pill>

          {selectedNode?.type === 'host' && (
            <Pill
              display="inline-flex"
              alignItems="center"
              backgroundColor="#E4E7EB"
              color="default"
              fontSize={12}
              paddingX={12}
              paddingY={8}
              borderRadius={12}
            >
              <Icon icon="application" marginRight={8} size={14} />
              <Strong>{overview.open_ports_count}</Strong>
              <Text marginLeft={4}>
                {overview.open_ports_count === 1 ? 'Port' : 'Ports'}
              </Text>
            </Pill>
          )}

          {(selectedNode?.type === 'host' || selectedNode?.type === 'domain' || selectedNode?.type === 'port') && (
            <Pill
              display="inline-flex"
              alignItems="center"
              backgroundColor="#E6F3FF"
              color="blue"
              fontSize={12}
              paddingX={12}
              paddingY={8}
              borderRadius={12}
            >
              <Icon icon="globe" marginRight={8} size={14} />
              <Strong>{overview.url_count || 0}</Strong>
              <Text marginLeft={4}>
                {(overview.url_count || 0) === 1 ? 'URL' : 'URLs'}
              </Text>
            </Pill>
          )}
        </Pane>
      
        {/* Sources Section */}
        {overview.sources?.length > 0 && (
      <Pane marginBottom={16}>
            <Strong size={400} color="default">Discovery Sources:</Strong>
            <Pane display="flex" gap={8} marginTop={8} flexWrap="wrap">
              {overview.sources.map(source => (
                <Badge
                  key={source}
                  backgroundColor="#F4F5F7"
                  color="default"
                  fontSize={12}
                  paddingX={8}
                  paddingY={4}
                  borderRadius={4}
                >
                  {source}
                </Badge>
          ))}
        </Pane>
      </Pane>
        )}

        {/* Last Scanned Section */}
        {overview.last_scanned && (
        <Pane marginBottom={16}>
            <Strong size={400} color="default">Last Scanned: </Strong>
            <Text color="default">
              {new Date(overview.last_scanned).toLocaleString()}
            </Text>
        </Pane>
      )}

        {/* Task History Section */}
        {overview.task_history?.length > 0 && (
          <Pane>
            <Heading size={500} marginY={12}>Task History</Heading>
            <Pane>
              {overview.task_history.map((task, index) => (
        <Pane
          key={task.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  backgroundColor={index % 2 === 0 ? '#F9F9FB' : 'white'}
          padding={8}
          borderRadius={4}
        >
                  <Pane>
                    <Text color="default" fontWeight={500}>
                      {task.task_type.replace('_', ' ')}
                    </Text>
            <Badge
              marginLeft={8}
              color={
                        task.status === 'COMPLETED' ? 'success' :
                        task.status === 'FAILED' ? 'danger' :
                        task.status === 'RUNNING' ? 'warning' :
                'neutral'
              }
            >
              {task.status}
            </Badge>
          </Pane>
                  <Text color="muted" size={300}>
                    {new Date(task.created_at).toLocaleString()}
            </Text>
        </Pane>
      ))}
            </Pane>
          </Pane>
        )}
    </Pane>
  );
  };

  const tabs = ['Overview', 'Ports', 'Notes', 'Findings'];
  
  // Filter tabs based on node type
  const getAvailableTabs = () => {
    if (selectedNode?.type === 'project') {
      return ['Overview'];
    }
    if (selectedNode?.type === 'country' || selectedNode?.type === 'org' || selectedNode?.type === 'asn') {
      return ['Overview'];
    }
    if (selectedNode?.type === 'host') {
      return tabs;
    }
    if (selectedNode?.type === 'domain' || selectedNode?.type === 'port') {
      return ['Overview', 'Notes', 'Findings'];
    }
    return tabs;
  };
  
  const availableTabs = getAvailableTabs();

  return (
    <Pane
      width={400}
      height="100%"
      position="absolute"
      right={0}
      top={0}
      background="white"
      borderLeft="1px solid #E6E8F0"
      display="flex"
      flexDirection="column"
      transform={`translateX(${selectedNode ? 0 : '100%'})`}
      transitionProperty="transform"
      transitionDuration="200ms"
      transitionTimingFunction="cubic-bezier(0.4, 0, 0.2, 1)"
    >
      {/* Header */}
      <Pane padding={16} borderBottom="1px solid #E6E8F0" display="flex" alignItems="center">
        <Pane flex={1}>
          <Heading size={600}>{selectedNode?.label}</Heading>
          <Badge color="blue">{selectedNode?.type}</Badge>
          {nodeData?.details?.severity && (
            <Badge 
              marginLeft={8} 
              backgroundColor={getSeverityColor(nodeData.details.severity)}
              color={nodeData.details.severity === 'critical' ? 'white' : 'black'}
            >
              {nodeData.details.severity}
            </Badge>
          )}
        </Pane>
        <IconButton icon="cross" appearance="minimal" onClick={onClose} />
      </Pane>

      {/* Tabs */}
      <Pane padding={16} borderBottom="1px solid #E6E8F0">
        <Tablist>
          {availableTabs.map((tab, index) => (
            <Tab
              key={tab}
              isSelected={selectedTab === index}
              onSelect={() => setSelectedTab(index)}
            >
              {tab}
            </Tab>
          ))}
        </Tablist>
      </Pane>

      {/* Content */}
      <Pane flex={1} overflow="auto" padding={16}>
        {loading ? (
          <Pane display="flex" alignItems="center" justifyContent="center" height="100%">
            <Spinner />
          </Pane>
        ) : (
          <>
            {selectedTab === 0 && (
              <Pane>
                {selectedNode?.type === 'project' && (
                  <Pane>
                    <Heading size={500} marginBottom={16}>Project Information</Heading>
                    <Text>{nodeData?.details?.description || 'No description available'}</Text>
                  </Pane>
                )}
                
                {selectedNode?.type === 'country' && (
                  <Pane>
                    <Heading size={500} marginBottom={16}>Country Information</Heading>
                    <Text>Hosts in {nodeData?.details?.name}: {nodeData?.details?.host_count || 0}</Text>
                  </Pane>
                )}
                
                {selectedNode?.type === 'org' && (
                  <Pane>
                    <Heading size={500} marginBottom={16}>Organization Information</Heading>
                    <Text>Organization: {nodeData?.details?.name}</Text>
                    <Text>Country: {nodeData?.details?.country}</Text>
                    <Text>Hosts: {nodeData?.details?.host_count || 0}</Text>
                  </Pane>
                )}
                
                {selectedNode?.type === 'asn' && (
                  <Pane>
                    <Heading size={500} marginBottom={16}>ASN Information</Heading>
                    <Text>ASN: {nodeData?.details?.name}</Text>
                    <Text>Organization: {nodeData?.details?.org}</Text>
                    <Text>Country: {nodeData?.details?.country}</Text>
                    <Text>Hosts: {nodeData?.details?.host_count || 0}</Text>
                  </Pane>
                )}
                
                {selectedNode?.type === 'host' && (
                  <>
                    <Heading size={500} marginBottom={16}>Actions</Heading>
                    <Pane display="flex" gap={8} marginBottom={24}>
                      <Button onClick={() => handleScanTask('NMAP_SCAN')}>
                        Nmap Scan
                      </Button>
                      <Button onClick={() => handleScanTask('NUCLEI_SCAN')}>
                        Nuclei Scan
                      </Button>
                    </Pane>
                  </>
                )}
                {renderOverviewSummary()}
              </Pane>
            )}

            {selectedTab === 1 && selectedNode?.type === 'host' && (
              <Pane>
                {nodeData?.ports?.map(port => (
                  <Pane
                    key={port.id}
                    padding={12}
                    marginBottom={8}
                    background="tint1"
                    borderRadius={4}
                  >
                    <Pane display="flex" alignItems="center">
                    <Strong>{port.number}/{port.protocol}</Strong>
                    {port.service && <Text marginLeft={8}>{port.service}</Text>}
                      <Badge 
                        marginLeft="auto" 
                        backgroundColor={getSeverityColor(port.severity)}
                        color={port.severity === 'critical' ? 'white' : 'black'}
                      >
                        {port.severity}
                      </Badge>
                    </Pane>
                  </Pane>
                ))}
              </Pane>
            )}

            {selectedTab === 2 && (
              <NoteEditor
                hostId={selectedNode?.type === 'host' ? selectedNode.id : undefined}
                domainId={selectedNode?.type === 'domain' ? selectedNode.id : undefined}
                portId={selectedNode?.type === 'port' ? selectedNode.id : undefined}
                compact={true}
              />
            )}

            {selectedTab === 3 && (
              <Pane>
                {nodeData?.findings?.map(finding => (
                  <Pane
                    key={finding.id}
                    padding={12}
                    marginBottom={8}
                    background="tint1"
                    borderRadius={4}
                  >
                    <Pane display="flex" alignItems="center" marginBottom={8}>
                      <Heading size={400}>{finding.name}</Heading>
                      <Badge
                        marginLeft={8}
                        backgroundColor={getSeverityColor(finding.severity)}
                        color={finding.severity === 'critical' ? 'white' : 'black'}
                      >
                        {finding.severity}
                      </Badge>
                    </Pane>
                    <Text color="muted">{finding.description}</Text>
                    {finding.template_id && (
                      <Badge marginTop={8}>Template: {finding.template_id}</Badge>
                    )}
                    {finding.url && (
                      <Text marginTop={8} color="blue500">
                        {finding.url}
                      </Text>
                    )}
                    <Text marginTop={8} color="muted">
                      Status: {finding.status}
                    </Text>
                  </Pane>
                ))}
              </Pane>
            )}
          </>
        )}
      </Pane>
    </Pane>
  );
};

export default SidePanel; 